import React from 'react';
import { useSettings } from '../context/SettingsProvider';
import { Languages } from 'lucide-react';
import { languages, type LanguageCode } from '../i18n';
import { useTranslation } from 'react-i18next';

interface LanguageSwitcherProps {
    /** Show as toggle (AR/EN) or dropdown */
    variant?: 'toggle' | 'dropdown';
    /** Additional class names */
    className?: string;
}

export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
    variant = 'toggle',
    className = '',
}) => {
    const { language, setLanguage } = useSettings();
    const { t } = useTranslation();

    // Toggle between AR and EN
    const toggleLanguage = () => {
        setLanguage(language === 'en' ? 'ar' : 'en');
    };

    // Simple toggle button
    if (variant === 'toggle') {
        return (
            <button
                onClick={toggleLanguage}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-accent transition-colors font-medium ${className}`}
                title={t('settings.language')}
                aria-label={t('settings.language')}
            >
                <Languages className="h-4 w-4" />
                <span className="text-sm">
                    {language === 'en' ? 'AR' : 'EN'}
                </span>
            </button>
        );
    }

    // Dropdown with all language options
    return (
        <div className={`flex items-center gap-1 p-1 bg-muted rounded-lg ${className}`}>
            {languages.map((lang) => (
                <button
                    key={lang.code}
                    onClick={() => setLanguage(lang.code as LanguageCode)}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${language === lang.code
                            ? 'bg-background shadow-sm text-foreground'
                            : 'text-muted-foreground hover:text-foreground'
                        }`}
                    title={lang.name}
                    aria-label={lang.name}
                >
                    {lang.nativeName}
                </button>
            ))}
        </div>
    );
};
